import type { IssueImpactSchema, IssueSchema } from '@shape-construction/api/src/types';
import type { MessageGetter } from 'localization/types';

/**
 * The client code uses two enumerations for Impact
 * 1. IssueImpactSchema (autogenerated) corresponds to the backend data model.
 * 2. ImpactType contains an additional 'notEntered' status,
 *    which is not passed to the server. If the actual impact is null,
 *    the client represents it using a virtual 'notEntered' status.
 */
export type ImpactType = IssueImpactSchema | 'notEntered';

export type ImpactField = keyof Pick<IssueSchema, 'impact' | 'workAffected' | 'dueDate' | 'delayStart' | 'delayFinish'>;

interface ImpactProps {
  value: ImpactType;
  label: string;
  groupingOptionLabel: string;
  description: string;
}

export type ImpactRecord = Record<ImpactType, ImpactProps>;

export const getImpactRecord = (t: MessageGetter): ImpactRecord => {
  const impactItems: ImpactType[] = ['notEntered', 'noEffect', 'potentialDelay', 'liveDelay', 'completedDelay'];
  return impactItems.reduce(
    (acc, val) => ({
      ...acc,
      [val]: {
        value: val,
        label: t(`issue.detail.impact.impact.${val}.label`),
        groupingOptionLabel: t(`issue.detail.impact.impact.${val}.groupingOptionLabel`),
        description: t(`issue.detail.impact.impact.${val}.description`),
      },
    }),
    {} as ImpactRecord
  );
};

export const impactFieldsMap: Record<ImpactType, ImpactField[]> = {
  notEntered: ['impact'],
  noEffect: ['impact', 'dueDate'],
  potentialDelay: ['impact', 'workAffected', 'dueDate'],
  liveDelay: ['impact', 'workAffected', 'delayStart'],
  completedDelay: ['impact', 'workAffected', 'delayStart', 'delayFinish'],
};

/**
 * Adding a status type to this array will display it on the `edit impact` modal.
 * The virtual `notEntered` status should not exist in this collection.
 */
export const ImpactValues: IssueImpactSchema[] = ['noEffect', 'potentialDelay', 'liveDelay', 'completedDelay'];

export const impactFields = (impact: IssueImpactSchema) => impactFieldsMap[impact] || ['impact'];
